<script setup lang="ts">
import { Redo2, Save, Undo2 } from 'lucide-vue-next'
import { useFlowEditorContext } from '../composables/useFlowEditor'

/** Undo/redo, manual checkpoints and the clickable history list (newest first). */
const { history } = useFlowEditorContext()
const { canUndo, canRedo, undo, redo, historyPointer, orderedHistoryEntries } = history
const applyHistoryEntry = history.goTo
const addCheckpoint = () => history.pushHistory('Checkpoint')
const formatTimestamp = (timestamp: number) => new Date(timestamp).toLocaleTimeString()
</script>

<template>
  <section class="panel flow-history-panel">
    <header class="panel__header">
      <h2>{{ $t('flow.history') }}</h2>
    </header>
    <div class="panel__body flow-history__body">
      <div class="flow-history__actions" role="toolbar" aria-label="Flow history actions">
        <button
          type="button"
          class="icon-btn"
          :disabled="!canUndo"
          :title="$t('flow.historyUndo')"
          @click="undo"
        >
          <Undo2 aria-hidden="true" />
        </button>
        <button
          type="button"
          class="icon-btn"
          :disabled="!canRedo"
          :title="$t('flow.historyRedo')"
          @click="redo"
        >
          <Redo2 aria-hidden="true" />
        </button>
        <button
          type="button"
          class="icon-btn"
          :title="$t('flow.historyAddCheckpoint')"
          @click="addCheckpoint"
        >
          <Save aria-hidden="true" />
        </button>
      </div>
      <ul class="history-list">
        <li v-for="item in orderedHistoryEntries" :key="`${item.entry.id}-top`">
          <button
            type="button"
            class="history-entry"
            :class="{ active: item.index === historyPointer }"
            @click="applyHistoryEntry(item.index)"
          >
            <div>
              <strong>{{ item.entry.label }}</strong>
              <p>{{ formatTimestamp(item.entry.timestamp) }}</p>
            </div>
          </button>
        </li>
      </ul>
    </div>
  </section>
</template>

<style scoped>
.flow-history-panel {
  flex: 1;
  min-height: 0;
}

.flow-history__body {
  overflow: auto;
  min-height: 0;
}

.flow-history__actions {
  display: inline-flex;
  gap: 0.3rem;
  margin-bottom: 0.5rem;
}

.history-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.history-entry {
  width: 100%;
  text-align: left;
  padding: 0.35rem 0.55rem;
  border: 1px solid #cbd5f5;
  background: #fff;
  border-radius: 0.55rem;
}

.history-entry.active {
  border-color: color-mix(in srgb, var(--pl-color-primary, #4338ca) 35%, #ffffff);
  background: color-mix(in srgb, var(--pl-color-primary, #4338ca) 10%, #ffffff);
}

.history-entry strong {
  font-size: 0.8rem;
}

.history-entry p {
  margin: 0.15rem 0 0;
  color: #475569;
  font-size: 0.7rem;
}
</style>

<style scoped src="./styles/iconButton.css"></style>
